import { useEffect } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

const SEOHead = ({ 
  title = "Smartborn - Enterprise AI & IT Services | 100+ Projects Delivered",
  description = "Transform your business with Smartborn's enterprise-grade AI & IT services. Custom software, cloud solutions, and offshore development. 200+ expert developers, 15+ years experience across 25+ industries.",
  keywords = "enterprise AI development, custom software development, IT consulting services, offshore software development, staff augmentation, cloud migration, digital transformation, SAP consulting, Salesforce implementation, Workday HCM, cybersecurity services, DevOps automation, mobile app development, machine learning solutions",
  canonical = "/",
  ogImage = "/og-image.png",
  ogType = "website",
  author = "Smartborn",
  publishedTime,
  modifiedTime
}: SEOHeadProps) => {
  
  useEffect(() => {
    // Update document title - max 60 characters for optimal SEO
    document.title = title.length > 60 ? title.substring(0, 57) + '...' : title;
    
    // Update meta description - max 160 characters for optimal SEO
    const metaDescription = document.querySelector('meta[name="description"]');
    const trimmedDescription = description.length > 160 ? description.substring(0, 157) + '...' : description;
    if (metaDescription) {
      metaDescription.setAttribute('content', trimmedDescription);
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords);
    }
    
    // Update author
    const metaAuthor = document.querySelector('meta[name="author"]');
    if (metaAuthor) {
      metaAuthor.setAttribute('content', author);
    }
    
    // Update canonical link
    const baseURL = window.location.origin;
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    const fullCanonical = canonical.startsWith('http') ? canonical : `${baseURL}${canonical}`;
    canonicalLink.setAttribute('href', fullCanonical);
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', title);
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) ogDescription.setAttribute('content', trimmedDescription);
    
    const ogImageMeta = document.querySelector('meta[property="og:image"]');
    const fullOgImage = ogImage.startsWith('http') ? ogImage : `${baseURL}${ogImage}`;
    if (ogImageMeta) ogImageMeta.setAttribute('content', fullOgImage);
    
    const ogTypeMeta = document.querySelector('meta[property="og:type"]');
    if (ogTypeMeta) ogTypeMeta.setAttribute('content', ogType);
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', fullCanonical);
    
    // Add article published/modified time for blog posts
    if (publishedTime) {
      let articlePublished = document.querySelector('meta[property="article:published_time"]');
      if (!articlePublished) {
        articlePublished = document.createElement('meta');
        articlePublished.setAttribute('property', 'article:published_time');
        document.head.appendChild(articlePublished);
      }
      articlePublished.setAttribute('content', publishedTime);
    }
    
    if (modifiedTime) {
      let articleModified = document.querySelector('meta[property="article:modified_time"]');
      if (!articleModified) {
        articleModified = document.createElement('meta');
        articleModified.setAttribute('property', 'article:modified_time');
        document.head.appendChild(articleModified);
      }
      articleModified.setAttribute('content', modifiedTime);
    }
    
    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', title);
    
    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) twitterDescription.setAttribute('content', trimmedDescription);
    
    const twitterImage = document.querySelector('meta[property="twitter:image"]');
    if (twitterImage) twitterImage.setAttribute('content', fullOgImage);
    
    // Add mobile web app capable meta tags
    let appleCapable = document.querySelector('meta[name="apple-mobile-web-app-capable"]');
    if (!appleCapable) {
      appleCapable = document.createElement('meta');
      appleCapable.setAttribute('name', 'apple-mobile-web-app-capable');
      appleCapable.setAttribute('content', 'yes');
      document.head.appendChild(appleCapable);
    }
    
    // Add theme color
    let themeColor = document.querySelector('meta[name="theme-color"]');
    if (!themeColor) {
      themeColor = document.createElement('meta');
      themeColor.setAttribute('name', 'theme-color');
      themeColor.setAttribute('content', '#FF9F40');
      document.head.appendChild(themeColor);
    }
    
  }, [title, description, keywords, canonical, ogImage, ogType, author, publishedTime, modifiedTime]);

  return null;
};

export default SEOHead;